import styled from '@emotion/styled';
import { DEFAULT_FIGURE_ALT_TEXT, TAG_FIGURE_IMAGE_CLASSNAME } from 'constants/altTextTagWidgets';
import { CSSProperties, useMemo } from 'react';
import { AccessibleElement } from 'types/widget.types';

import { useWidget } from './useWidget';

interface TransparentImgProps {
  style?: CSSProperties;
}

// 1x1px transparent png image
const TRANSPARENT_DATA_URI =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

const StyledImg = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
  top: 0;
  left: 0;
`;

/**
 * TRANSPARENT IMAGE COMPONENT
 *
 * Renders a transparent image with alt text if specified
 */
export const TransparentImg = ({ style = {} }: TransparentImgProps) => {
  const { widgetId, altText, isDecorative } = useWidget<AccessibleElement>();

  // Do not render the image if it is decorative
  if (isDecorative) return <></>;

  // Render the transparent image and alt text will be a " " (empty space) if it is ""
  return <TransparentImageBase style={style} id={widgetId} altText={altText || DEFAULT_FIGURE_ALT_TEXT} />;
};

type TransparentImageBaseProps = TransparentImgProps & { id: string; altText: string };

export const TransparentImageBase = ({ style = {}, id, altText }: TransparentImageBaseProps) => {
  /**
   * For generating multiple image object in the pdf file, It adds the id in the data URI.
   * It's not going to affect to the browser's img render.
   */
  const dataURL = useMemo(() => {
    return `${TRANSPARENT_DATA_URI.slice(0, 14)}:${id}${TRANSPARENT_DATA_URI.slice(14)}`;
  }, [id]);

  // a11y-figure-image className used on the export2/src/utils/pdf.ts to filter the decorative image.
  return <StyledImg src={dataURL} style={style} alt={altText} className={TAG_FIGURE_IMAGE_CLASSNAME} />;
};
